import { api, publicApi } from './axios';

/**
 * Duel API — клиент для работы с дуэлями
 *
 * V1 API: Полное управление дуэлями (требует авторизации)
 * V2 API: Публичный доступ с возрастной фильтрацией
 * @see api_duel_reference.md
 */

// ==================== V2 API - Публичный доступ ====================

/**
 * [V2] Получить публичный список дуэлей с пагинацией.
 * Не требует авторизации. Автоматическая фильтрация по возрастным ограничениям.
 * @param {number} [page=0] - Номер страницы
 * @param {number} [size=10] - Размер страницы
 * @returns {Promise<Object>} Пагинированный список публичных дуэлей
 */
export const getPublicDuels = (page = 0, size = 10) => {
    return publicApi.get('/duels/public', { params: { page, size } }).then((res) => res.data);
};

/**
 * [V2] Получить публичные активные дуэли с пагинацией.
 * Не требует авторизации. Автоматическая фильтрация по возрастным ограничениям.
 * @param {number} [page=0] - Номер страницы
 * @param {number} [size=10] - Размер страницы
 * @returns {Promise<Object>} Пагинированный список активных дуэлей
 */
export const getPublicActiveDuels = (page = 0, size = 10) => {
    return publicApi.get('/duels/public/active', { params: { page, size } }).then((res) => res.data);
};

/**
 * [V2] Получить детали дуэли.
 * Требуется авторизация. Проверяется возрастной доступ.
 * @param {string} id - ID дуэли
 * @returns {Promise<Object>} Детали дуэли с расширенной информацией
 */
export const getDuelDetails = (id) => {
    return api.get(`/duels/${id}`).then((res) => res.data);
};

/**
 * [V2] Получить доступ к дуэли в режиме зрителя.
 * Требуется авторизация. Проверяется isSpectatorAllowed и возрастной доступ.
 * @param {string} id - ID дуэли
 * @returns {Promise<Object>} Данные дуэли для зрителя
 */
export const spectateDuel = (id) => {
    return api.get(`/duels/${id}/spectate`).then((res) => res.data);
};

/**
 * [V2] Проверить доступ пользователя к дуэли.
 * Требуется авторизация для определения возраста пользователя.
 * @param {string} id - ID дуэли
 * @returns {Promise<{duelId: string, hasAccess: boolean, userAge: number, ageRestriction: number, reason: string}>}
 */
export const checkDuelAccess = (id) => {
    return api.get(`/duels/${id}/access`).then((res) => res.data);
};

// ==================== V1 API - Управление дуэлями ====================

/**
 * Создать дуэль
 * @param {Object} data - Данные дуэли
 * @param {string} data.title - Название дуэли
 * @param {string} [data.description] - Описание
 * @param {string} [data.coverImageId] - ID обложки
 * @param {string} data.format - Формат: SOLO, TEAM_2V2, TEAM_3V3
 * @param {string} data.roundType - Тип раундов: SINGLE_ROUND, BEST_OF_THREE
 * @param {number} [data.ageRestriction] - Возрастное ограничение: 0, 13, 16, 18
 * @param {boolean} [data.isSpectatorAllowed] - Разрешить зрителей (по умолчанию: true)
 * @param {boolean} [data.isPublic] - Публичная дуэль (по умолчанию: true)
 * @param {DuelVotingSystem} [data.votingSystem] - Система голосования: PLUS_MINUS_ZERO, NUMERIC_10, CRITERIA_BASED
 * @param {DuelCriteriaPreset} [data.criteriaPreset] - Пресет критериев для CRITERIA_BASED
 * @returns {Promise<Object>} Созданная дуэль
 */
export const createDuel = (data) => {
    return api.post('/duels', data).then((res) => res.data);
};

/**
 * Получить список дуэлей с пагинацией
 * @param {number} [page=0] - Номер страницы
 * @param {number} [size=10] - Размер страницы
 * @returns {Promise<Object>} Пагинированный список дуэлей
 */
export const getDuels = (page = 0, size = 10) => {
    return api.get('/duels', { params: { page, size } }).then((res) => res.data);
};

/**
 * Получить активные дуэли
 * @param {number} [page=0] - Номер страницы
 * @param {number} [size=10] - Размер страницы
 * @returns {Promise<Object>} Пагинированный список активных дуэлей
 */
export const getActiveDuels = (page = 0, size = 10) => {
    return api.get('/duels/active', { params: { page, size } }).then((res) => res.data);
};

/**
 * Получить мои дуэли
 * @param {number} [page=0] - Номер страницы
 * @param {number} [size=10] - Размер страницы
 * @returns {Promise<Object>} Пагинированный список моих дуэлей
 */
export const getMyDuels = (page = 0, size = 10) => {
    return api.get('/duels/my', { params: { page, size } }).then((res) => res.data);
};

/**
 * Получить детали дуэли
 * @param {string} id - ID дуэли
 * @returns {Promise<Object>} Детали дуэли
 */
export const getDuelById = (id) => {
    return api.get(`/duels/${id}`).then((res) => res.data);
};

/**
 * Обновить дуэль
 * @param {string} id - ID дуэли
 * @param {Object} data - Данные для обновления
 * @param {string} [data.title] - Новое название
 * @param {string} [data.description] - Новое описание
 * @param {string} [data.coverImageId] - Новый ID обложки
 * @returns {Promise<Object>} Обновлённая дуэль
 */
export const updateDuel = (id, data) => {
    return api.put(`/duels/${id}`, data).then((res) => res.data);
};

/**
 * Удалить дуэль
 * @param {string} id - ID дуэли
 * @returns {Promise<void>}
 */
export const deleteDuel = (id) => {
    return api.delete(`/duels/${id}`).then((res) => res.data);
};

/**
 * Начать дуэль
 * @param {string} id - ID дуэли
 * @returns {Promise<Object>} Дуэль со статусом ACTIVE
 */
export const startDuel = (id) => {
    return api.patch(`/duels/${id}/start`).then((res) => res.data);
};

/**
 * Получить результаты дуэли
 * @param {string} id - ID дуэли
 * @returns {Promise<Object>} Результаты дуэли
 */
export const getDuelResults = (id) => {
    return api.get(`/duels/${id}/results`).then((res) => res.data);
};

// ==================== Раунды ====================

// ==================== Раунды ====================

/**
 * Создать раунд в дуэли (только создатель, до начала)
 * @param {string} duelId - ID дуэли
 * @param {Object} data - Данные раунда
 * @param {string} [data.theme] - Тема раунда
 * @param {string} [data.task] - Задание раунда
 * @param {number} [data.duration] - Длительность в секундах
 * @param {DuelVotingSystem} [data.votingSystem] - Система голосования для раунда
 * @param {DuelCriteriaPreset} [data.criteriaPreset] - Пресет критериев
 * @param {Array} [data.limitations] - Ограничения раунда
 * @returns {Promise<Object>} Созданный раунд
 */
export const createRound = (duelId, data) => {
    return api.post(`/duels/${duelId}/rounds`, data).then((res) => res.data);
};

/**
 * Обновить раунд (тема, задание и система голосования)
 * @param {string} duelId - ID дуэли
 * @param {string} roundId - ID раунда
 * @param {Object} data - Данные для обновления
 * @param {string} [data.theme] - Тема раунда
 * @param {string} [data.task] - Задание раунда
 * @param {DuelVotingSystem} [data.votingSystem] - Система голосования
 * @param {DuelCriteriaPreset} [data.criteriaPreset] - Пресет критериев
 * @returns {Promise<Object>} Обновлённый раунд
 */
export const updateRound = (duelId, roundId, data) => {
    return api.put(`/duels/${duelId}/rounds/${roundId}`, data).then((res) => res.data);
};

/**
 * Завершить раунд и определить победителя
 * @param {string} duelId - ID дуэли
 * @param {string} roundId - ID раунда
 * @param {Object} data - Данные завершения
 * @param {string} [data.winnerId] - ID победителя
 * @param {string} [data.decisionReason] - Причина решения
 * @param {boolean} [data.autoStartNextRound] - Автоматически начать следующий раунд
 * @returns {Promise<Object>} Завершённый раунд
 */
export const completeRound = (duelId, roundId, data) => {
    return api.post(`/duels/${duelId}/rounds/${roundId}/complete`, data).then((res) => res.data);
};

// ==================== Работы (DuelWork) ====================
// Важно: endpoints /tracks и /tracks/upload возвращают DuelWorkResponse,
// который содержит вложенный track (DuelTrackResponse) и text (DuelTextResponse).

/**
 * Загрузить работу в раунд (JSON, файл предварительно загружен).
 * Возвращает DuelWorkResponse с вложенным track.
 * Поддерживает три типа дуэлей:
 * - Audio: только fileId
 * - Text: только lyrics
 * - Hybrid: fileId + lyrics
 * @param {string} duelId - ID дуэли
 * @param {string} roundId - ID раунда
 * @param {Object} data - Данные работы (SubmitTrackRequest)
 * @param {string} [data.fileId] - ID аудиофайла (обязателен для audio/hybrid)
 * @param {string} [data.lyrics] - Текст трека (обязателен для text/hybrid, макс. 10000 символов)
 * @param {string} data.text - Текст трека (обязательное поле)
 * @param {string} [data.comment] - Комментарий к треку (макс. 1000 символов)
 * @returns {Promise<Object>} DuelWorkResponse — работа с вложенным track
 */
export const submitTrack = (duelId, roundId, data) => {
    return api.post(`/duels/${duelId}/rounds/${roundId}/tracks`, data).then((res) => res.data);
};

/**
 * Загрузить аудио работу с файлом (multipart).
 * Возвращает DuelWorkResponse с вложенным track.
 * @param {string} duelId - ID дуэли
 * @param {string} roundId - ID раунда
 * @param {File} file - Аудиофайл (MP3, WAV, OGG, M4A, FLAC, макс. 50MB)
 * @param {string} [comment] - Комментарий к треку
 * @param {string} [text] - Текст трека (обязательное поле)
 * @returns {Promise<Object>} DuelWorkResponse — работа с вложенным track
 */
export const uploadTrack = (duelId, roundId, file, comment, text) => {
    const formData = new FormData();
    formData.append('file', file);
    if (text) {
        formData.append('text', text);
    } else {
        // text — обязательное поле на сервере
        formData.append('text', '');
    }
    if (comment) {
        formData.append('comment', comment);
    }
    return api.post(`/duels/${duelId}/rounds/${roundId}/tracks/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }).then((res) => res.data);
};

/**
 * Получить работы раунда.
 * Возвращает массив DuelWorkResponse с вложенными track/text.
 * @param {string} duelId - ID дуэли
 * @param {string} roundId - ID раунда
 * @returns {Promise<Array<Object>>} Массив DuelWorkResponse
 */
export const getRoundTracks = (duelId, roundId) => {
    return api.get(`/duels/${duelId}/rounds/${roundId}/tracks`).then((res) => res.data);
};

/**
 * Получить трек по ID (общий endpoint, без привязки к раунду).
 * @param {string} trackId - ID трека
 * @returns {Promise<Object>} DuelTrackResponse
 */
export const getTrackById = (trackId) => {
    return api.get(`/duels/tracks/${trackId}`).then((res) => res.data);
};

/**
 * Удалить трек.
 * @param {string} trackId - ID трека
 * @returns {Promise<void>}
 */
export const deleteTrack = (trackId) => {
    return api.delete(`/duels/tracks/${trackId}`).then((res) => res.data);
};

/**
 * Обновить комментарий к треку.
 * @param {string} trackId - ID трека
 * @param {string} comment - Новый комментарий
 * @returns {Promise<Object>} Обновлённый трек
 */
export const updateTrackComment = (trackId, comment) => {
    return api.put(`/duels/tracks/${trackId}/comment`, null, { params: { comment } }).then((res) => res.data);
};

// ==================== Голосование судей ====================

/**
 * Голосование судьи за участника в раунде
 * @param {string} duelId - ID дуэли
 * @param {string} roundId - ID раунда
 * @param {Object} data - Данные голоса
 * @param {string} data.participantId - ID участника
 * @param {string} [data.voteReason] - Причина голоса
 * @param {number} [data.voteWeight] - Вес голоса
 * @returns {Promise<Object>} Созданный голос
 */
export const judgeVote = (duelId, roundId, data) => {
    return api.post(`/duels/${duelId}/rounds/${roundId}/judge-vote`, data).then((res) => res.data);
};

/**
 * Обновить голос судьи
 * @param {string} duelId - ID дуэли
 * @param {string} roundId - ID раунда
 * @param {Object} data - Данные для обновления
 * @param {string} data.participantId - ID участника
 * @param {string} [data.voteReason] - Причина голоса
 * @param {number} [data.voteWeight] - Вес голоса
 * @returns {Promise<Object>} Обновлённый голос
 */
export const updateJudgeVote = (duelId, roundId, data) => {
    return api.put(`/duels/${duelId}/rounds/${roundId}/judge-vote`, data).then((res) => res.data);
};

/**
 * Удалить голос судьи
 * @param {string} duelId - ID дуэли
 * @param {string} roundId - ID раунда
 * @returns {Promise<void>}
 */
export const deleteJudgeVote = (duelId, roundId) => {
    return api.delete(`/duels/${duelId}/rounds/${roundId}/judge-vote`).then((res) => res.data);
};

/**
 * Получить статистику голосования в раунде
 * @param {string} duelId - ID дуэли
 * @param {string} roundId - ID раунда
 * @returns {Promise<Object>} Статистика голосования
 */
export const getVotingStats = (duelId, roundId) => {
    return api.get(`/duels/${duelId}/rounds/${roundId}/voting-stats`).then((res) => res.data);
};

/**
 * Получить результаты голосования
 * @param {string} duelId - ID дуэли
 * @param {string} roundId - ID раунда
 * @returns {Promise<Object>} Результаты голосования
 */
export const getVotingResults = (duelId, roundId) => {
    return api.get(`/duels/${duelId}/rounds/${roundId}/voting-results`).then((res) => res.data);
};

/**
 * Проверить статус голосования (все ли судьи проголосовали)
 * @param {string} duelId - ID дуэли
 * @param {string} roundId - ID раунда
 * @returns {Promise<Object>} Статус голосования
 */
export const getVotingStatus = (duelId, roundId) => {
    return api.get(`/duels/${duelId}/rounds/${roundId}/voting-status`).then((res) => res.data);
};

/**
 * Загрузить обложку дуэли
 * @param {string} id - ID дуэли
 * @param {File} file - Изображение (JPEG, PNG, WebP, макс. 10MB)
 * @returns {Promise<Object>} Данные обложки
 */
export const uploadDuelCover = (id, file) => {
    const formData = new FormData();
    formData.append('cover', file);
    return api.post(`/duels/${id}/cover`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }).then((res) => res.data);
};

/**
 * Удалить обложку дуэли
 * @param {string} id - ID дуэли
 * @returns {Promise<void>}
 */
export const deleteDuelCover = (id) => {
    return api.delete(`/duels/${id}/cover`).then((res) => res.data);
};

/**
 * Получить информацию об обложке дуэли
 * @param {string} id - ID дуэли
 * @returns {Promise<Object>} Данные обложки
 */
export const getDuelCover = (id) => {
    return api.get(`/duels/${id}/cover`).then((res) => res.data);
};

// ==================== Приглашения ====================

/**
 * Отправить приглашение в дуэль
 * @param {string} duelId - ID дуэли
 * @param {Object} data - Данные приглашения
 * @param {string} data.profileId - ID профиля приглашаемого
 * @param {string} data.role - Роль: PARTICIPANT или JUDGE
 * @param {string} [data.message] - Сообщение к приглашению
 * @returns {Promise<Object>} Созданное приглашение
 */
export const sendDuelInvite = (duelId, data) => {
    return api.post(`/duels/${duelId}/invites`, data).then((res) => res.data);
};

/**
 * Получить приглашения дуэли
 * @param {string} duelId - ID дуэли
 * @returns {Promise<Array>} Список приглашений
 */
export const getDuelInvites = (duelId) => {
    return api.get(`/duels/${duelId}/invites`).then((res) => res.data);
};

/**
 * Получить мои приглашения
 * @returns {Promise<Array>} Список моих приглашений
 */
export const getMyDuelInvites = () => {
    return api.get('/duels/invites/my').then((res) => res.data);
};

/**
 * Принять приглашение
 * @param {string} inviteId - ID приглашения
 * @returns {Promise<Object>} Обновлённое приглашение
 */
export const acceptDuelInvite = (inviteId) => {
    return api.post(`/duels/invites/${inviteId}/accept`).then((res) => res.data);
};

/**
 * Отклонить приглашение
 * @param {string} inviteId - ID приглашения
 * @returns {Promise<Object>} Обновлённое приглашение
 */
export const declineDuelInvite = (inviteId) => {
    return api.patch(`/duels/invites/${inviteId}/decline`).then((res) => res.data);
};

/**
 * Отозвать приглашение
 * @param {string} inviteId - ID приглашения
 * @returns {Promise<Object>} Обновлённое приглашение
 */
export const revokeDuelInvite = (inviteId) => {
    return api.delete(`/duels/invites/${inviteId}`).then((res) => res.data);
};

export default {
    getPublicDuels,
    getPublicActiveDuels,
    getDuelDetails,
    spectateDuel,
    checkDuelAccess,
    createDuel,
    getDuels,
    getActiveDuels,
    getMyDuels,
    getDuelById,
    updateDuel,
    deleteDuel,
    startDuel,
    getDuelResults,
    createRound,
    updateRound,
    completeRound,
    submitTrack,
    uploadTrack,
    getRoundTracks,
    getTrackById,
    deleteTrack,
    updateTrackComment,
    uploadDuelCover,
    deleteDuelCover,
    getDuelCover,
    judgeVote,
    updateJudgeVote,
    deleteJudgeVote,
    getVotingStats,
    getVotingResults,
    getVotingStatus,
    sendDuelInvite,
    getDuelInvites,
    getMyDuelInvites,
    acceptDuelInvite,
    declineDuelInvite,
    revokeDuelInvite
};

